/**
 * The privileged surface.
 *
 * Everything in this file is a call into Cloud Functions, and everything in
 * this file is here for the same reason: it cannot be trusted to a browser.
 * Enrolling somebody sends mail to a real address. Dispatching an announcement
 * fans out to a whole class. Retrying a delivery spends a provider's quota.
 * None of those can be undone by a rules check after the fact, so none of them
 * are reachable from the client at all — the rules refuse the underlying
 * writes outright, and these callables are the only door.
 *
 * The functions re-verify the caller's identity and role from the ID token.
 * They do not trust anything in the payload about who is asking.
 */

import { httpsCallable, type HttpsCallableResult } from 'firebase/functions';
import { functions } from '../lib/firebase';

/** Thrown with a message worth putting in front of a person. */
export class CallableError extends Error {
  override readonly name = 'CallableError';
  constructor(
    message: string,
    readonly code: string
  ) {
    super(message);
  }
}

/**
 * Wrap a callable so failures arrive as something the interface can show.
 *
 * Firebase surfaces errors as `functions/permission-denied` with the raw
 * server message attached; that is right for a log and wrong for a person.
 */
function callable<Req, Res>(name: string): (payload: Req) => Promise<Res> {
  const fn = httpsCallable<Req, Res>(functions, name);
  return async (payload: Req) => {
    try {
      const result: HttpsCallableResult<Res> = await fn(payload);
      return result.data;
    } catch (err) {
      const e = err as { code?: string; message?: string };
      const code = (e.code ?? 'unknown').replace(/^functions\//, '');
      throw new CallableError(friendlyMessage(code, e.message), code);
    }
  };
}

function friendlyMessage(code: string, raw?: string): string {
  switch (code) {
    case 'permission-denied':
      return 'Your account is not allowed to do that.';
    case 'unauthenticated':
      return 'Your session expired. Sign in again and try once more.';
    case 'failed-precondition':
      // These carry a specific, deliberately-worded reason from the server —
      // "this semester is archived", "no notification group is attached".
      return raw || 'That is not possible in the current state.';
    case 'already-exists':
      return raw || 'That already exists.';
    case 'not-found':
      return raw || 'That no longer exists.';
    case 'resource-exhausted':
      return 'Too many requests in a row. Wait a moment and try again.';
    case 'deadline-exceeded':
    case 'unavailable':
      return 'The server did not respond. Check your connection and try again.';
    default:
      return raw || 'Something went wrong on the server.';
  }
}

// ── Enrollment ───────────────────────────────────────────────────────────────

export interface EnrollResult {
  uid: string;
  email: string;
  /** False when the person was already enrolled, so nothing was re-sent. */
  notified: boolean;
}

/**
 * Give a family account access to a semester.
 *
 * This is the only way enrollment happens. It resolves the address to an
 * account, writes the enrollment, and sends the welcome mail and push. It is
 * idempotent: enrolling someone twice does not notify them twice.
 */
export const enrollStudent = callable<
  { semesterId: string; email: string },
  EnrollResult
>('enrollStudent');

/** Remove access. Sends nothing — being un-enrolled is not an announcement. */
export const unenrollStudent = callable<{ semesterId: string; uid: string }, { ok: true }>(
  'unenrollStudent'
);

// ── Publishing ───────────────────────────────────────────────────────────────

/**
 * Publish an announcement, now or at a future instant.
 *
 * `publishAt` in the future leaves the item as a draft and lets the scheduler
 * flip it — which is what guarantees the notification goes out at publication
 * rather than at authoring time.
 */
export const publishAnnouncement = callable<
  { semesterId: string; announcementId: string; publishAt?: string | null },
  { published: boolean; scheduledFor: string | null; notified: number }
>('publishAnnouncement');

export const unpublishAnnouncement = callable<
  { semesterId: string; announcementId: string },
  { ok: true }
>('unpublishAnnouncement');

/**
 * Mark coursework complete and tell the class.
 *
 * Completion is a callable rather than a field edit precisely because it
 * notifies: an email, a push and a bulletin item, all of them to the course's
 * notification group and nobody else. Toggling it back off does not un-send
 * anything, and does not re-send if it is completed again — the server keeps
 * the stamp.
 *
 * A grade *changing* later is a different event with a wider audience, and it
 * is not raised here; see the trigger in functions/src/index.ts.
 */
export const setTodoCompletion = callable<
  { semesterId: string; todoId: string; completed: boolean; completedAt?: string | null },
  { notified: number }
>('setTodoCompletion');

/**
 * Publish a document.
 *
 * Notifies only if the semester has already started; see
 * shared/notificationRules.ts for why.
 */
export const publishDocument = callable<
  { semesterId: string; documentId: string },
  { notified: number; suppressed: boolean }
>('publishDocument');

// ── Notification groups ──────────────────────────────────────────────────────

export const createNotificationGroup = callable<
  { name: string; semesterId: string | null },
  { id: string }
>('createNotificationGroup');

export const updateNotificationGroup = callable<
  { groupId: string; name?: string; memberUids?: string[]; isDefault?: boolean },
  { ok: true }
>('updateNotificationGroup');

export const deleteNotificationGroup = callable<{ groupId: string }, { ok: true }>(
  'deleteNotificationGroup'
);

// ── Delivery operations ──────────────────────────────────────────────────────

/** Queue a failed delivery for another attempt, right now. */
export const retryDelivery = callable<{ deliveryId: string }, { ok: true }>('retryDelivery');

/** Retry everything currently in a failed state, for one event or globally. */
export const retryFailedDeliveries = callable<
  { eventId?: string | null },
  { queued: number }
>('retryFailedDeliveries');

// ── Push subscriptions ───────────────────────────────────────────────────────

export const registerPushSubscription = callable<
  { endpoint: string; p256dh: string; auth: string; device: string },
  { id: string }
>('registerPushSubscription');

export const unregisterPushSubscription = callable<{ endpoint: string }, { ok: true }>(
  'unregisterPushSubscription'
);

/** Sends a push to the caller's own devices, so the setup can be proven. */
export const sendTestPush = callable<Record<string, never>, { sent: number; failed: number }>(
  'sendTestPush'
);

export interface GradeTestResult {
  /** The exact line that went out, so the console can show it back. */
  headline: string;
  term: string;
  email: { ok: boolean; blocked: boolean; error: string | null };
  push: { sent: number; failed: number };
}

/**
 * Rehearse a grade notification against your own address and devices.
 *
 * The real template and the real wording, sent to the caller and nobody else.
 * It writes no delivery records and touches no coursework, so it can neither
 * pollute the delivery log nor suppress a genuine notification. Every field is
 * optional — omitted ones fall back to the worked example.
 */
export const sendTestGradeNotification = callable<
  {
    assignment?: string;
    courseName?: string;
    grade?: string;
    semesterId?: string | null;
  },
  GradeTestResult
>('sendTestGradeNotification');

// ── Semester operations ──────────────────────────────────────────────────────

/**
 * Copy a semester's structure into a new term.
 *
 * Structure only — courses, document categories, naming. Never the content:
 * no coursework, submissions, announcements, agenda entries, uploads,
 * enrollments, groups, seen state or delivery history. The new term starts
 * genuinely empty.
 */
export const duplicateSemester = callable<
  { semesterId: string; term: string; startDate: string; endDate: string },
  { semesterId: string; coursesCopied: number; categoriesCopied: number }
>('duplicateSemester');

/**
 * Reopen an archived semester for editing, until an explicit instant.
 *
 * Has no effect on student access: the date window is what governs that, and
 * this deliberately does not touch it.
 */
export const reopenSemester = callable<
  { semesterId: string; until: string },
  { reopenedUntil: string }
>('reopenSemester');

export const closeSemester = callable<{ semesterId: string }, { ok: true }>('closeSemester');

/**
 * Delete a semester and everything inside it.
 *
 * The only irreversible operation in the product. `confirm` carries the typed
 * word from the dialog and is re-checked server-side — the last guard on
 * something unrecoverable does not belong in a browser.
 */
export const deleteSemester = callable<
  { semesterId: string; confirm: string },
  { documentsDeleted: number; filesDeleted: number }
>('deleteSemester');

// ── Activity ─────────────────────────────────────────────────────────────────

/**
 * Record that a student opened something.
 *
 * Called by the client because only the client knows what is on screen, but
 * written by the server so the trail cannot be forged or erased. Fire and
 * forget: a failure here must never interrupt reading.
 */
export const recordView = callable<
  { semesterId: string; contentType: string; contentId: string; label: string },
  { ok: true }
>('recordView');

/** Stamped once per session, on sign-in. */
export const recordLogin = callable<Record<string, never>, { ok: true }>('recordLogin');

export const syncProfessorDirectory = callable<Record<string, never>, { added: number }>('syncProfessorDirectory');

/** Only a manually added comment can raise this notification. */
export const addSubmissionComment = callable<
  {semesterId: string; todoId: string; commentId: string; professor: string; body: string},
  {notified: number; suppressed: boolean}
>('addSubmissionComment');
